import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './Button';
import { Icons } from './Icons';
import { NotificationBell } from './NotificationBell';
import { BrandLogo } from './BrandLogo';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { useSidebar } from './sidebar/SidebarContext';

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, location.hash]);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const logoHref = user ? '/dashboard' : '/';

  return (
    <header className="force-light bg-surface/80 backdrop-blur-md sticky top-0 border-b border-outline-variant/30 shadow-sm z-50">
      <div className="flex justify-between items-center px-6 py-3 w-full max-w-7xl mx-auto h-auto">
        <div className="flex items-center gap-8">
          <BrandLogo variant="full" size="xl" href={logoHref} />
          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/features" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">Features</NavLink>
            <NavLink to="/catalog" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">Designs</NavLink>
            <NavLink to="/pricing" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">Pricing</NavLink>
            <NavLink to="/company#about" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">About</NavLink>
            <NavLink to="/company#contact" className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">Support</NavLink>
          </nav>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <Link
            to="/cart"
            aria-label="Cart"
            className="w-10 h-10 flex items-center justify-center hover:bg-surface-low rounded-xl transition-colors text-on-surface-variant hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <Icons.Cart size={20} />
          </Link>
          {user ? (
            <>
              <Link to="/dashboard">
                <Button variant="ghost">Dashboard</Button>
              </Link>
              <Button variant="outline" onClick={handleLogout}>Log Out</Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost">Log In</Button>
              </Link>
              <Link to="/signup">
                <Button>Sign Up</Button>
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="md:hidden text-primary p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
          aria-label="Toggle mobile menu"
        >
          <Icons.Menu size={24} />
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {createPortal(
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="force-light fixed inset-0 z-[100] bg-surface/95 backdrop-blur-xl md:hidden flex flex-col"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-outline-variant/30">
                <BrandLogo variant="full" size="xl" href={logoHref} />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-on-surface-variant p-2 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
                  aria-label="Close mobile menu"
                >
                  <Icons.X size={24} />
                </button>
              </div>
              
              <nav className="flex flex-col px-6 py-8 gap-6 overflow-y-auto flex-1">
                <NavLink to="/features" className="text-xl font-medium text-on-surface hover:text-primary transition-colors">Features</NavLink>
                <NavLink to="/catalog" className="text-xl font-medium text-on-surface hover:text-primary transition-colors">Designs</NavLink>
                <NavLink to="/pricing" className="text-xl font-medium text-on-surface hover:text-primary transition-colors">Pricing</NavLink>
                <NavLink to="/company#about" className="text-xl font-medium text-on-surface hover:text-primary transition-colors">About</NavLink>
                <NavLink to="/company#contact" className="text-xl font-medium text-on-surface hover:text-primary transition-colors">Support</NavLink>
                <NavLink to="/cart" className="text-xl font-medium text-on-surface hover:text-primary transition-colors flex items-center gap-2">
                  <Icons.Cart size={22} />
                  <span>Cart</span>
                </NavLink>
                
                <div className="mt-8 flex flex-col gap-4">
                  {user ? (
                    <>
                      <Link to="/dashboard" className="w-full">
                        <Button variant="primary" size="lg" className="w-full text-lg">Dashboard</Button>
                      </Link>
                      <Button variant="outline" size="lg" className="w-full text-lg" onClick={handleLogout}>Log Out</Button>
                    </>
                  ) : (
                    <>
                      <Link to="/login" className="w-full">
                        <Button variant="outline" size="lg" className="w-full text-lg">Log In</Button>
                      </Link>
                      <Link to="/signup" className="w-full">
                        <Button size="lg" className="w-full text-lg">Sign Up</Button>
                      </Link>
                    </>
                  )}
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}

export function DashboardHeader({ title, description }: { title?: string; description?: string }) {
  const { openMobile } = useSidebar();

  return (
    <header className="flex justify-between items-center px-4 md:px-8 py-4 sticky top-0 bg-surface-lowest/80 backdrop-blur-md z-30">
      <div className="flex items-center gap-3">
        <button
          onClick={openMobile}
          aria-label="Open navigation"
          aria-controls="sidebar"
          className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl text-on-surface-variant hover:bg-surface-low transition-colors"
        >
          <Icons.Menu size={22} />
        </button>
        <div className="md:hidden flex items-center">
          <BrandLogo variant="full" size="md" />
        </div>
        {title ? (
          <div className="flex flex-col">
            <h1 className="text-2xl font-bold text-on-surface">{title}</h1>
            {description && <p className="text-sm text-on-surface-variant">{description}</p>}
          </div>
        ) : <div />}
      </div>

      <div className="flex items-center gap-4 text-primary">
        <Link
          to="/cart"
          aria-label="Cart"
          className="w-10 h-10 flex items-center justify-center hover:bg-surface-low rounded-xl transition-colors text-on-surface-variant hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <Icons.Cart size={20} />
        </Link>
        <button
          className="w-10 h-10 flex items-center justify-center hover:bg-surface-low rounded-xl transition-colors text-on-surface-variant focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="Search"
        >
          <Icons.Search size={20} />
        </button>
        <NotificationBell />
      </div>
    </header>
  );
}
